import React, { FC } from 'react';
import { TopUpDialogProps } from '../model/topup.types';
import { useTopUpViewModel } from '../viewmodel/useTopUpViewModel';
import { Elements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';


const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(stripePublishableKey || '');

const TopUpDialogContent: FC<Pick<TopUpDialogProps, 'onClose' | 'onSubmit'>> = ({
  onClose,
  onSubmit,
}) => {
  const { state, handleAmountChange, handleSubmit, handleReset } =
    useTopUpViewModel(onSubmit);

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleOverlayClick = () => {
    handleClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleOverlayClick}
      />

      {/* Modal */}
      <div className="relative bg-white w-96 rounded-2xl shadow-xl p-6 z-10">
        <h2 className="text-xl font-semibold mb-4">Top Up Amount</h2>

        <input
          type="number"
          placeholder="Enter amount"
          value={state.amount}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleAmountChange(e.target.value)
          }
          disabled={state.isSubmitting}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
        />

        <div className="mt-4 border border-gray-300 rounded-lg px-3 py-3">
          <CardElement />
        </div>

        {state.error && (
          <p className="text-red-500 text-sm mt-2">{state.error}</p>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            disabled={state.isSubmitting}
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={state.isSubmitting}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {state.isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TopUpDialog: FC<TopUpDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  if (!isOpen) return null;

  if (!stripePromise) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white w-96 rounded-2xl shadow-xl p-6 z-10">
          <h2 className="text-xl font-semibold mb-2">Top Up Amount</h2>
          <p className="text-red-500 text-sm">
            Stripe is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY.
          </p>
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <TopUpDialogContent onClose={onClose} onSubmit={onSubmit} />
    </Elements>
  );
};

export default TopUpDialog;